
import type { VoiceLevelSummary } from '../../utils/voice-level';

// Augment the global Window interface to include our custom debug logging flag
declare global {
  interface Window {
    __debugLogging?: boolean;
  }
}

function generateRandomString(length: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  return result;
}

// Helper function to check if logging should be enabled
function shouldLog(): boolean {
  try {
    return window.__debugLogging === true;
  } catch (e) {
    // If window is not available (e.g. in Node.js), disable logging
    return false;
  }
}

class ReadLogParametersFront {
    private static instance: ReadLogParametersFront;
    private constructor() { }

    public log_session_id: string = "";

    public static getInstance(): ReadLogParametersFront {
        if (!ReadLogParametersFront.instance) {
            ReadLogParametersFront.instance = new ReadLogParametersFront();
        }
        return ReadLogParametersFront.instance;
    }
}

export function buildLogStartRecording(
  textEntityId?: number
): any {
  ReadLogParametersFront.getInstance().log_session_id = generateRandomString(30)
  
  // Log only if recording parameter exists in URL
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-START_RECORDING:ENGINE2-TEXT-ENTITY-ID:', textEntityId)
  }

  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'START_RECORDING',
      log_text: "SPEECHMATICS",
      user_id: -1,
      log_value: textEntityId
    };
}

/**
 * How a reading started: one row per stream, so first-word misses can be split
 * by cold start and by position in a flow, and by how long each step took.
 * Times are ms from the start of startRecording; readyMs is when the server
 * confirmed the recogniser is listening (null if it never said so).
 */
export function buildLogStartTiming(details: {
  path: 'cold' | 'warm';
  flowPosition: number;
  micReadyMs: number;
  readyMs: number | null;
  readyTimedOut: boolean;
  gateOpenMs: number;
  mobile: boolean;
  /** The microphone and recogniser were opened while the verse was on screen, before the button. */
  preopened: boolean;
  /** The recogniser setting the server reported (e.g. "enhanced"), so models can be compared. */
  operatingPoint: string;
  /** The hostname the reading came from: dev and prod write to the same table. */
  site: string;
}): any {
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'READING_START_TIMING',
      log_text: JSON.stringify({
        path: details.path,
        flow_position: details.flowPosition,
        mic_ready_ms: details.micReadyMs,
        ready_ms: details.readyMs,
        ready_timed_out: details.readyTimedOut,
        gate_open_ms: details.gateOpenMs,
        mobile: details.mobile,
        preopened: details.preopened,
        operating_point: details.operatingPoint,
        site: details.site,
      }),
      user_id: -1,
      log_value: String(details.gateOpenMs),
    };
}

/**
 * How loud the reader was: one row per stream, written when the stream ends.
 * log_text is the JSON summary (median RMS dBFS while listening, percent of
 * 100 ms frames that were quiet or clipping, speak-up prompts shown, frame
 * count); log_value is the median, for quick sorting.
 */
export function buildLogVoiceLevel(summary: VoiceLevelSummary): any {
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'READING_VOICE_LEVEL',
      log_text: JSON.stringify(summary),
      user_id: -1,
      log_value: summary.median_dbfs === null ? null : String(summary.median_dbfs),
    };
}

export function buildLogBatchRecognitionStart(
  correct: string[],
  candidate: string[],
  candidateTolerance?: number,
  correctStart?: number,
): any {
    if (shouldLog()) {
      console.log(new Date(), ':ENGINE2-EVAL-SKIP-TOLERANCE:', candidateTolerance);
      console.log(new Date(), ':ENGINE2-EVAL-START-AT:', correctStart);
      console.log(new Date(), ':ENGINE2-EVAL-TARGET:', correct.join(' '));
      console.log(new Date(), ':ENGINE2-EVAL-USER-TRANSCRIPTION:', candidate.join(' '));
    }

  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'BATCH_RECOGNITION_START',
      log_text: `{
      SPMTCS-EVAL-SKIP-TOLERANCE: ${candidateTolerance},
      SPMTCS-EVAL-START-AT: ${correctStart}, 
      SPMTCS-EVAL-TARGET: ${correct.join(' ')},
      SPMTCS-EVAL-USER-TRANSCRIPTION: ${candidate.join(' ')}
      }`,
      user_id: -1,
      log_value: candidateTolerance
    };
}

export function buildLogEvalWord(
  correctWord?: string,
  candidateWord?: string | null,
  score?: number,
  status?: string,
  startTime?: number,
  endTime?: number,
  speechmaticsScore?: number,
): any {
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-EVAL-WORD:', `  "${correctWord}" → score: ${(score ?? 0).toFixed(3)}`, status,
      `start: ${startTime ?? 'n/a'}`, `end: ${endTime ?? 'n/a'}`, `sm: ${speechmaticsScore ?? 'n/a'}`);
  }
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'EVAL_WORD',
      // הפסק (Correct / Mistake / Missing / Pending) נשמר כשדה שלישי.
      // בלי זה בשורה נשמר רק ציון הדמיון של בדיקה אחת בשרשרת, ואי אפשר לדעת
      // מהמסד מה התלמיד באמת קיבל על המילה - רק לנחש, וזה מה שקרה עד עכשיו.
      log_text: correctWord+"||"+candidateWord+"||"+(status ?? ''),
      user_id: -1,
      log_value: (score ?? 0).toFixed(3),
      start_time: startTime ?? null,
      end_time: endTime ?? null,
      // Speechmatics' own confidence, kept apart from log_value (our similarity
      // against the expected word). null when it was never per-word.
      speechmatics_score: speechmaticsScore ?? null
    };
}

export function buildLogTranscription(
  text?: string,
  type?: string
): any {
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-TRANSCRIPTION:', text);
  }
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'TRANSCRIPTION',
      log_text: text,
      user_id: -1,
      log_value: type
    };
}

export function buildLogEndConditionStats(
  correctWords: number,
  failedWords: number,
  pendingWords: number,
  accuracyPercentage: number
): any {
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-END-CONDITION-STATS:', accuracyPercentage);
  }
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'SPMTCS_END_CONDITION_STATS',
      log_text: `${correctWords}||${failedWords}||${pendingWords}`,
      user_id: -1,
      log_value: accuracyPercentage
    };
}

export function buildLogBackendFinish(): any {
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-BACKEND-FINISH:');
  }
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'BACKEND_FINISH',
      log_text: null,
      user_id: -1,
      log_value: null
    };
}
export function buildLogFrontendFinish(): any {
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-FRONTEND-FINISH:');
  }
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'FRONTEND_FINISH',
      log_text: null,
      user_id: -1,
      log_value: null
    };
}

export function buildLogEndSession(): any {
  if (shouldLog()) {
    console.log(new Date(), ':ENGINE2-END-SESSION:', ReadLogParametersFront.getInstance().log_session_id);
  }
  return {
      created_at: new Date(),
      session_id: ReadLogParametersFront.getInstance().log_session_id,
      log_type: 'END_SESSION',
      log_text: null,
      user_id: -1,
      log_value: null
    };
}
